import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
} from '@angular/forms';
import { isEqual } from '@epicuro-next/utilities';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { IRange } from '../range.interface';

/**
 * Multi Range Slider component
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'atx-multi-range-slider',
  templateUrl: './multi-range-slider.component.html',
  styleUrls: ['./multi-range-slider.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiRangeSliderComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class MultiRangeSliderComponent
  implements AfterViewInit, ControlValueAccessor, OnChanges
{
  /** getter for range minimum label */
  public get rangeMinLabel() {
    return this.formatLabel(this.value?.min);
  }

  /** getter for range maximum label */
  public get rangeMaxLabel() {
    return (
      this.formatLabel(this.value?.max) +
      (this.displayedMax === this.value?.max ? '+' : '')
    );
  }

  /** getter for minimum label */
  public get minLabel() {
    return this.formatLabel(this.min);
  }

  /** getter for maximum label */
  public get maxLabel() {
    return this.formatLabel(this.maxDisplayed) + (this.displayedMax ? '+' : '');
  }

  public get maxDisplayed() {
    return this.displayedMax && this.max > this.displayedMax
      ? this.displayedMax
      : this.max;
  }

  public rangeSliderForm = this.fb.group({
    minimum: [''],
    maximum: [''],
  });

  private destroy$ = new Subject<void>();

  /** Dependencies. */
  constructor(private cdr: ChangeDetectorRef, private fb: UntypedFormBuilder) {
    this.rangeChanged$.pipe(debounceTime(300)).subscribe(() =>
      this.rangeChanged.emit({
        ...{
          min: this.value.min,
          max: this.value.max === this.displayedMax ? this.max : this.value.max,
        },
        unit: this.unit,
      }),
    );
  }
  /** progress bar reference */
  @ViewChild('progress') public progress: ElementRef;
  /** tooltip low reference */
  @ViewChild('tooltipLow') public tooltipLow: ElementRef;
  /** tooltip high reference */
  @ViewChild('tooltipHigh') public tooltipHigh: ElementRef;
  /** output which informs about range changes */
  @Output() public rangeChanged = new EventEmitter<IRange>();
  /** subject used to apply a debounce */
  public debouncer: Subject<string> = new Subject<string>();

  /** min range value */
  @Input() public min: number;
  /** max range value */
  @Input() public max: number;
  /** range step */
  @Input() public step = 1;
  /** unit to be displayed with min and max labels */
  @Input() public unit = '';
  /** throttle unit to be displayed with min and max labels */
  @Input() public displayedMax: number;
  @Input()
  public multiRangeSliderInputs = false;

  /** current selected range */
  public value: IRange;

  /** left limit in percentage for background for showing the current selection */
  private progressLow: number;
  /** right limit in percentage for background for showing the current selection */
  private progressHigh: number;
  /** range changed subject used to introduce a debounce */
  private rangeChanged$ = new Subject<void>();

  public disabled = false;

  public formatLabel(value: number | undefined) {
    return value !== undefined ? `${value}${this.unit}` : '';
  }

  /**
   * Function to trigger onChange
   */
  public onChange: (arg: IRange) => void = () => {};

  /**
   * Function to trigger onTouched
   */
  public onTouch: (arg: IRange) => void = () => {};

  /** after view init hook method */
  public ngAfterViewInit(): void {
    this.updateProgress();
    this.subscribeInputChanges();
  }
  /**
   * change hook method:
   */
  public ngOnChanges() {
    if (this.min >= 0 && this.maxDisplayed) {
      this.updateRange(true);
    }
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** write value */
  public writeValue(value: IRange): void {
    if (value === null) {
      return;
    }
    this.value.min = value.min;
    this.value.max = value.max > 0 ? value.max : this.maxDisplayed;

    if (this.min !== undefined && this.maxDisplayed !== undefined) {
      this.updateRange(true);
      this.cdr.detectChanges();
    }
  }
  /**
   * Required to implement ControlValueAccessor
   * @param fn The function to run onChange
   */
  public registerOnChange(fn: (arg: IRange) => void): void {
    this.onChange = fn;
  }

  /**
   * Required to implement ControlValueAccessor
   * @param fn The function to run onTouched
   */
  public registerOnTouched(fn: (arg: IRange) => void): void {
    this.onTouch = fn;
  }

  /**
   * method used to update numerically the range
   *
   * @param isMinInput distinguishes between min and max inputs
   * @param rangeLow range low percentage
   * @param rangeHigh range high percentage
   */
  private updateRange(
    isMinInput: boolean,
    rangeLow?: number,
    rangeHigh?: number,
  ) {
    if (!rangeLow) {
      rangeLow = this.value ? this.value.min : this.min;
    }

    if (!rangeHigh) {
      rangeHigh = this.value ? this.value.max : this.maxDisplayed;
    }

    const values = this.constrainRangeSliderValues(
      isMinInput,
      rangeLow,
      rangeHigh,
    );
    this.value = {
      min: values[0],
      max: values[1],
    };

    if (this.multiRangeSliderInputs) {
      const maxToRender =
        this.value.max === this.displayedMax
          ? this.rangeMaxLabel
          : this.value.max;
      this.rangeSliderForm.patchValue(
        {
          minimum: this.value.min,
          maximum: maxToRender,
        },
        { emitEvent: false },
      );
    }

    this.onChange(this.value);
    this.onTouch(this.value);
    this.updateProgress();
  }

  public emitRange() {
    this.rangeChanged$.next();
  }

  /** method called by the template when the user drag the slider */
  public onDrag($event: Event, isMinInput: boolean) {
    const changedValue = parseInt(
      ($event.target as HTMLInputElement).value,
      10,
    );
    this.updateRange(
      isMinInput,
      isMinInput ? changedValue : this.value.min,
      !isMinInput ? changedValue : this.value.max,
    );
  }

  public updateSliderValue(value: string, isMinInput: boolean) {
    let changedValue = parseInt(value, 10);
    if (isNaN(changedValue)) {
      changedValue = isMinInput ? this.min : this.max;
    }
    this.updateRange(
      isMinInput,
      isMinInput ? changedValue : this.value.min,
      !isMinInput ? changedValue : this.value.max,
    );
    this.rangeChanged$.next();
  }
  /** method called to redraw the progress caused by a change in the range selection */
  private updateProgress() {
    if (!this.progress || !this.value) {
      return;
    }

    this.progressLow = parseFloat(
      (
        100 *
        ((this.value.min - this.min) / (this.maxDisplayed - this.min))
      ).toFixed(2),
    );
    this.progressHigh = parseFloat(
      (
        100 *
        ((this.value.max - this.min) / (this.maxDisplayed - this.min))
      ).toFixed(2),
    );

    this.progress.nativeElement.style.setProperty(
      '--range-slider-low',
      `${this.progressLow}%`,
    );
    this.progress.nativeElement.style.setProperty(
      '--range-slider-high',
      `${this.progressHigh}%`,
    );
    this.tooltipLow?.nativeElement?.style.setProperty(
      '--tooltip-low-width',
      `calc(${this.progressLow}% + 20px)`,
    );
    this.tooltipHigh?.nativeElement?.style.setProperty(
      '--tooltip-high-width',
      `${this.progressHigh - this.progressLow}%`,
    );
  }

  /**
   * Method used to constrain the progress bar in the right position.
   * @param isMinInput flag to check if you are dragging a specific thumb RTL or LTR.
   * When the distance between low and high is very short, dragging one of them
   * moves the other of one step.
   */
  private constrainRangeSliderValues(
    isMinInput: boolean,
    rangeLow: number,
    rangeHigh: number,
  ) {
    if (this.min > rangeLow) {
      rangeLow = this.min;
    }

    if (this.maxDisplayed <= rangeLow) {
      rangeLow = this.maxDisplayed - this.step;
    }

    if (this.min >= rangeHigh) {
      rangeHigh = this.min + this.step;
    }

    if (this.maxDisplayed < rangeHigh) {
      rangeHigh = this.maxDisplayed;
    }

    if (!isMinInput && rangeLow >= rangeHigh) {
      rangeLow = rangeHigh - this.step;
    }

    if (isMinInput && rangeHigh <= rangeLow) {
      rangeHigh = rangeLow + this.step;
    }

    return [rangeLow, rangeHigh];
  }

  /**
   * set disabled state if the input status is changed
   */
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  private subscribeInputChanges() {
    if (this.multiRangeSliderInputs) {
      this.rangeSliderForm.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged(isEqual),
          debounceTime(1200),
        )
        .subscribe(({ minimum, maximum }) => {
          this.updateSliderValue(
            minimum.toString().length > 0 ? minimum : this.min,
            true,
          );
          this.updateSliderValue(
            maximum.toString().length > 0 ? maximum : this.max,
            false,
          );
        });
    }
  }
}
